/**
 * bodyMaterials — per-body render materials resolved from the material
 * library (+ optional color override), cached by appearance key.
 *
 * Procedural textures (brushed streaks, matte speckle) are tiny generated
 * canvases used as roughness maps over box-projected UVs — no image assets,
 * no network. Metals only read as metal with an environment to reflect; when
 * the viewport couldn't install one, displayed metalness is capped so parts
 * stay visible under the analytic lights.
 */

import * as THREE from 'three/webgpu';

import type { MaterialDef } from '@core/materials';
import { BODY_MATERIAL } from './meshView';

export interface BodyAppearance {
  material?: MaterialDef;
  color?: string;
}

/** Stable cache/diff key for an appearance ('' = default look). */
export function appearanceKey(a: BodyAppearance | null | undefined): string {
  if (!a) return '';
  return `${a.material?.id ?? ''}|${a.color ?? ''}`;
}

let envAvailable = false;

/** Viewport reports whether scene.environment made it in (metals need it). */
export function configureBodyMaterials(opts: { environment: boolean }): void {
  envAvailable = opts.environment;
  cache.clear(); // metalness caps changed — rebuild lazily
}

const textures = new Map<string, THREE.CanvasTexture>();

/** Grayscale noise canvas → tiling roughness modulation texture. */
function proceduralTexture(kind: 'brushed' | 'speckle'): THREE.CanvasTexture {
  let tex = textures.get(kind);
  if (tex) return tex;
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const img = ctx.createImageData(size, size);
  // Deterministic LCG so every session renders identically.
  let seed = 0x9e3779b9;
  const rand = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
  if (kind === 'speckle') {
    for (let i = 0; i < size * size; i++) {
      const v = 190 + rand() * 65; // 0.75..1.0 — subtle matte grain
      img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = 255;
    }
  } else {
    // Brushed: per-row base value with slow along-row wander → streaks.
    for (let y = 0; y < size; y++) {
      const base = 170 + rand() * 70;
      let wander = 0;
      for (let x = 0; x < size; x++) {
        wander = wander * 0.9 + (rand() - 0.5) * 24;
        const v = Math.max(140, Math.min(255, base + wander));
        const i = y * size + x;
        img.data[i * 4] = img.data[i * 4 + 1] = img.data[i * 4 + 2] = v;
        img.data[i * 4 + 3] = 255;
      }
    }
  }
  ctx.putImageData(img, 0, 0);
  tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  textures.set(kind, tex);
  return tex;
}

const cache = new Map<string, THREE.MeshStandardMaterial>();

/** The material a body renders with (shared instance per appearance). */
export function bodyMaterialFor(a: BodyAppearance | null | undefined): THREE.MeshStandardMaterial {
  const key = appearanceKey(a);
  if (key === '') return BODY_MATERIAL;
  let m = cache.get(key);
  if (m) return m;
  const app = a!.material?.appearance;
  m = new THREE.MeshStandardMaterial({
    color: new THREE.Color(a!.color ?? app?.color ?? '#b7bfcc'),
    metalness: Math.min(app?.metalness ?? 0.15, envAvailable ? 1 : 0.6),
    roughness: app?.roughness ?? 0.45,
  });
  if (app?.opacity !== undefined && app.opacity < 1) {
    m.transparent = true;
    m.opacity = app.opacity;
    m.depthWrite = false;
    m.side = THREE.DoubleSide;
  }
  if (app?.texture) m.roughnessMap = proceduralTexture(app.texture);
  cache.set(key, m);
  return m;
}
